import Button, {ButtonShape, ButtonSize, ButtonVariant} from "@components/Button";
import Icon from "@components/Icon";
import {useParsedBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import React from "react";

import {useReadableSlotTime} from "../../../../../hooks/useReadableSlotTime";
import {useTranslation} from "@i18n/client";
import {I18nNamespace} from "@i18n";
import {checkIfSameDay} from "../../../../../utils/timeUtils";

/**
 * The DatePicker looks something like:
 *
 *  (<-)     Today, April 14     (->)
 *
 */
type DatePickerProps = {
  currentCalendarTime: number;
  onMinusDay: () => void;
  onPlusDay: () => void;
  selectedLocationFirstLine?: string;
  timezone: string;
};

const DatePicker = ({
  currentCalendarTime,
  onMinusDay,
  onPlusDay,
  selectedLocationFirstLine,
  timezone,
}: DatePickerProps) => {
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const isSameDay = checkIfSameDay(currentCalendarTime, Date.now(), timezone);
  const readableCalendarTime = useReadableSlotTime(currentCalendarTime, {
    withoutHour: true,
    timezone,
  });
  const {rescheduling} = useParsedBookingQuery();

  return (
    <div className="flex w-full items-center justify-between gap-2">
      <Button
        shape={ButtonShape.CIRCLE}
        variant={ButtonVariant.FILL}
        size={ButtonSize.SM}
        onClick={onMinusDay}
        disabled={isSameDay}
        ariaLabel={t("Previous day", {ns: "web-scheduling"})}
        data-cy="previous-day"
      >
        <Icon icon="arrow-left3" />
      </Button>
      <div className="flex flex-col text-center gap-1">
        <p className="font-ttb text-lg leading-none" data-testid="current-date">
          {readableCalendarTime}
        </p>
        {selectedLocationFirstLine && !rescheduling && (
          <p className="font-ttr text-sm leading-none">{selectedLocationFirstLine}</p>
        )}
      </div>
      <Button
        shape={ButtonShape.CIRCLE}
        variant={ButtonVariant.FILL}
        size={ButtonSize.SM}
        onClick={onPlusDay}
        ariaLabel={t("Next day", {ns: "web-scheduling"})}
        data-cy="next-day"
      >
        <Icon icon="arrow-right3" />
      </Button>
    </div>
  );
};

export default React.memo(DatePicker);
